import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { 
  LayoutDashboard, 
  Trophy, 
  Receipt, 
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff,
  Wallet,
  FileText,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/trader', icon: LayoutDashboard, label: 'Markets', exact: true },
  { path: '/trader/bets', icon: Receipt, label: 'My Bets' },
  { path: '/trader/report', icon: FileText, label: 'Report' },
  { path: '/trader/password', icon: Lock, label: 'Password' },
];

export function TraderLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isConnected } = useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="trader-layout">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-zinc-900 border-b border-zinc-800 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-zinc-400 hover:text-zinc-100"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-blue-500" />
            <span className="font-heading font-bold text-zinc-100">Bookmaker</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Balance Display - Only show if enabled */}
          {user?.show_exposure !== false && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 rounded border border-zinc-700">
              <Wallet className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-sm font-bold text-zinc-100" data-testid="balance-display">
                €{user?.balance?.toFixed(2) || '0.00'}
              </span>
            </div>
          )}
          
          {/* Connection Status */}
          <div className="flex items-center gap-1">
            {isConnected ? (
              <Wifi className="w-4 h-4 text-emerald-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <aside 
        className={cn(
          "fixed top-14 left-0 h-[calc(100%-3.5rem)] w-64 bg-zinc-900 border-r border-zinc-800 z-40",
          "transform transition-transform duration-200 ease-in-out",
          "lg:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors",
                isActive(item.path, item.exact)
                  ? "bg-blue-600/20 text-blue-400 border border-blue-600/30"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
              )}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-zinc-100">{user?.username}</p>
              <p className="text-xs text-zinc-500 uppercase">{user?.role}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full border-zinc-700 text-zinc-400 hover:text-zinc-100"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Desktop Bottom Nav */}
      <nav className="hidden lg:flex fixed bottom-0 left-0 right-0 h-14 bg-zinc-900 border-t border-zinc-800 items-center justify-center gap-8 z-40">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded text-sm transition-colors",
              isActive(item.path, item.exact)
                ? "bg-blue-600/20 text-blue-400"
                : "text-zinc-400 hover:text-zinc-100"
            )}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </Link>
        ))}
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-zinc-400 hover:text-zinc-100"
          onClick={handleLogout}
          data-testid="logout-btn-desktop"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </nav>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="pt-14 pb-0 lg:pb-14 min-h-screen">
        <div className="p-4 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
